type RGB = [number, number, number];
const parse = (value: string, fallback: string): RGB => {
  let hex = value.replace(/^#/, "");
  if (/^[\da-f]{3}$/i.test(hex)) hex = hex.split("").map(c => c + c).join("");
  if (!/^[\da-f]{6}$/i.test(hex)) hex = fallback;
  return [0, 2, 4].map(i => parseInt(hex.slice(i, i + 2), 16)) as RGB;
};
const hex = (rgb: RGB) => "#" + rgb.map(c => Math.round(c).toString(16).padStart(2, "0")).join("");
const mix = (a: RGB, b: RGB, amount: number): RGB => a.map((v, i) => Math.round(v * (1 - amount) + b[i] * amount)) as RGB;
const luminance = (rgb: RGB) => rgb.map(v => { const c = v / 255; return c <= .04045 ? c / 12.92 : ((c + .055) / 1.055) ** 2.4; }).reduce((sum, c, i) => sum + c * [.2126, .7152, .0722][i], 0);
const contrast = (a: RGB, b: RGB) => (Math.max(luminance(a), luminance(b)) + .05) / (Math.min(luminance(a), luminance(b)) + .05);
const black: RGB = [0, 0, 0];
const white: RGB = [255, 255, 255];
const ink: RGB = [7, 17, 31];
function readable(background: RGB): RGB {
  const best = contrast(ink, background) >= contrast(white, background) ? ink : white;
  return contrast(best, background) >= 4.5 ? best : black;
}
/** Resolve opaque colors once, including muted text and interactive states. */
export function resolveReadableColors(primaryColor: string, backgroundColor: string, textColor: string) {
  const background = parse(backgroundColor, "ffffff");
  let primary = parse(primaryColor, "275cf6");
  const requested = parse(textColor, "171b24");
  const text = contrast(requested, background) >= 4.5 ? requested : readable(background);
  let muted = text;
  for (let percent = 74; percent <= 100; percent++) {
    muted = mix(background, text, percent / 100);
    if (contrast(muted, background) >= 4.5) break;
  }
  const max = Math.max(...primary), min = Math.min(...primary);
  const saturated = max > 0 && (max - min) / max >= .25;
  const primaryText = saturated && luminance(primary) <= .35 ? white : readable(primary);
  const original = primary;
  for (let percent = 0; contrast(primaryText, primary) < 4.5 && percent <= 100; percent++) {
    primary = mix(original, primaryText === white ? black : white, percent / 100);
  }
  const primaryHover = mix(primary, primaryText === white ? black : white, primaryText === white ? .14 : .08);
  return {
    background: hex(background), text: hex(text), mutedText: hex(muted),
    primary: hex(primary), primaryText: hex(primaryText), primaryHover: hex(primaryHover),
    primaryBorder: hex(contrast(primary, background) >= 3 ? primary : text),
  };
}
